import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Tooltip,
    Typography,
} from '@mui/material';
import type { SxProps } from '@mui/system';
import type { Theme } from '@mui/material/styles';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TagIcon from '@mui/icons-material/Tag';
import { keyframes } from '@mui/system';
import JSDB, { ApiCall } from '../../../fromKotlin/nk';
import WhatsAppQRDialog from './WhatsAppQRDialog';
import { getWhatsappConfig, isOfficialHubServer } from '../../../hooks/useWhatsAppConfig';
import {
    fetchWhatsAppConnected,
    isWhatsAppServerSkipConnectionCheck,
} from '../../../hooks/whatsappConnection';
import { resetHashIsraelStoredTokens } from '../../../api/customer/hashIsrael.api';

const HASH_ISRAEL_AUTH_URL =
    'https://openapi.taxes.gov.il/shaam/production/longtimetoken/oauth2/authorize' +
    '?response_type=code&client_id=35828e73b8b9edbc05c4ac943f5634f0' +
    '&scope=invoices_scope&redirect_uri=https://solvitcompany.com/reports/tax-israel';

const flashOpacity = keyframes`
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.35; }
`;

type HashIsraelState = 'connected' | 'disconnected' | null;
type AggressiveRefreshTarget = 'whatsapp' | 'hash-israel' | null;

const checkHashIsraelStatus = async (): Promise<HashIsraelState> => {
    try {
        const req = new ApiCall('REFRESH_HASH_ISRAEL');
        const result = await req.execute('LOGIN_FAILED' as any);
        if (result?.second?.name === 'LOGIN_FAILED' || result?.name === 'LOGIN_FAILED') {
            return 'disconnected';
        }
        return 'connected';
    } catch {
        return 'disconnected';
    }
};

const AGGRESSIVE_POLL_MS = 10 * 1000;
const AGGRESSIVE_WINDOW_MS = 5 * 60 * 1000;
const NORMAL_POLL_MS = 60 * 60 * 1000;

export type ConnectionStatusIndicatorsProps = {
    /** Merged into the outer flex container (display/gap/alignItems are set by default). */
    sx?: SxProps<Theme>;
};

/** Same connection UX as the header: WhatsApp health + QR / barcode flow, חשבונית ישראל. */
export function ConnectionStatusIndicators({ sx }: ConnectionStatusIndicatorsProps) {
    const waConfig = useMemo(() => getWhatsappConfig(), []);
    const hasWhatsAppConfig = !!waConfig;
    const skipWhatsAppConnectionCheck = isWhatsAppServerSkipConnectionCheck(waConfig?.server);
    const hasHashIsraelConfig = useMemo(() => JSDB().getUser()?.first?.hesh_israel == 1, []);

    const [isWhatsAppConnected, setIsWhatsAppConnected] = useState<boolean | null>(null);
    const [hashIsraelState, setHashIsraelState] = useState<HashIsraelState>(null);
    const [aggressiveRefreshUntil, setAggressiveRefreshUntil] = useState<number>(0);
    const [aggressiveRefreshTarget, setAggressiveRefreshTarget] = useState<AggressiveRefreshTarget>(null);
    const [qrDialogOpen, setQrDialogOpen] = useState(false);
    const [hashIsraelResetOpen, setHashIsraelResetOpen] = useState(false);
    const [hashIsraelResetting, setHashIsraelResetting] = useState(false);
    const [hashIsraelResetError, setHashIsraelResetError] = useState<string | null>(null);

    const checkWhatsApp = useCallback(async (): Promise<boolean> => {
        if (!waConfig) return false;
        const connected = await fetchWhatsAppConnected(waConfig);
        setIsWhatsAppConnected(connected);
        return connected;
    }, [waConfig]);

    const checkHashIsrael = useCallback(async (): Promise<HashIsraelState> => {
        const status = await checkHashIsraelStatus();
        setHashIsraelState(status);
        return status;
    }, []);

    useEffect(() => {
        if (hasWhatsAppConfig && skipWhatsAppConnectionCheck) {
            setIsWhatsAppConnected(true);
        } else if (hasWhatsAppConfig) {
            checkWhatsApp();
        }
        if (hasHashIsraelConfig) checkHashIsrael();

        const interval = setInterval(() => {
            if (hasWhatsAppConfig && !skipWhatsAppConnectionCheck) checkWhatsApp();
            if (hasHashIsraelConfig) checkHashIsrael();
        }, NORMAL_POLL_MS);

        return () => clearInterval(interval);
    }, [
        hasWhatsAppConfig,
        hasHashIsraelConfig,
        skipWhatsAppConnectionCheck,
        checkWhatsApp,
        checkHashIsrael,
    ]);

    useEffect(() => {
        if (aggressiveRefreshUntil <= 0 || !aggressiveRefreshTarget) return;

        const runPoll = async () => {
            if (Date.now() >= aggressiveRefreshUntil) {
                setAggressiveRefreshUntil(0);
                setAggressiveRefreshTarget(null);
                return;
            }

            if (aggressiveRefreshTarget === 'whatsapp') {
                if (!hasWhatsAppConfig) {
                    setAggressiveRefreshUntil(0);
                    setAggressiveRefreshTarget(null);
                    return;
                }
                if (skipWhatsAppConnectionCheck) {
                    setIsWhatsAppConnected(true);
                    setAggressiveRefreshUntil(0);
                    setAggressiveRefreshTarget(null);
                    return;
                }
                const connected = await checkWhatsApp();
                if (connected) {
                    setAggressiveRefreshUntil(0);
                    setAggressiveRefreshTarget(null);
                }
                return;
            }

            if (aggressiveRefreshTarget === 'hash-israel') {
                if (!hasHashIsraelConfig) {
                    setAggressiveRefreshUntil(0);
                    setAggressiveRefreshTarget(null);
                    return;
                }
                const status = await checkHashIsrael();
                if (status === 'connected') {
                    setAggressiveRefreshUntil(0);
                    setAggressiveRefreshTarget(null);
                }
            }
        };

        runPoll();
        const interval = setInterval(runPoll, AGGRESSIVE_POLL_MS);
        return () => clearInterval(interval);
    }, [
        aggressiveRefreshUntil,
        aggressiveRefreshTarget,
        hasWhatsAppConfig,
        hasHashIsraelConfig,
        skipWhatsAppConnectionCheck,
        checkWhatsApp,
        checkHashIsrael,
    ]);

    const handleWhatsAppDisconnectedClick = () => {
        if (!waConfig) return;
        if (isWhatsAppServerSkipConnectionCheck(waConfig.server)) {
            setIsWhatsAppConnected(true);
            return;
        }

        setAggressiveRefreshTarget('whatsapp');
        setAggressiveRefreshUntil(Date.now() + AGGRESSIVE_WINDOW_MS);

        if (waConfig.isNewApi) {
            // Open QR dialog inside the app
            setQrDialogOpen(true);
        } else {
            // Old: open external page
            const form = document.createElement('form');
            form.action = `${waConfig.baseUrl}/control/create`;
            form.method = 'POST';
            form.target = '_blank';
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'session';
            input.value = waConfig.instanceId;
            form.appendChild(input);
            document.body.appendChild(form);
            form.submit();
            document.body.removeChild(form);
        }
    };

    const handleHashIsraelClick = () => {
        if (hashIsraelState === 'connected') {
            setHashIsraelResetError(null);
            setHashIsraelResetOpen(true);
            return;
        }
        if (hashIsraelState === 'disconnected') {
            setAggressiveRefreshTarget('hash-israel');
            setAggressiveRefreshUntil(Date.now() + AGGRESSIVE_WINDOW_MS);
            const newWindow = window.open(HASH_ISRAEL_AUTH_URL, '_blank', 'noopener,noreferrer');
            if (newWindow) newWindow.opener = null;
        }
    };

    const handleHashIsraelResetConfirm = async () => {
        setHashIsraelResetting(true);
        setHashIsraelResetError(null);
        const result = await resetHashIsraelStoredTokens();
        setHashIsraelResetting(false);
        if (result.ok) {
            setHashIsraelResetOpen(false);
            setHashIsraelState('disconnected');
            void checkHashIsrael();
            return;
        }
        setHashIsraelResetError(result.error);
    };

    const disconnectedSx = {
        backgroundColor: '#f44336',
        cursor: 'pointer',
        '& .MuiChip-label': { color: '#fff' },
        '& .MuiChip-icon': { color: '#fff' },
        animation: `${flashOpacity} 1s ease-in-out infinite`
    };

    if (!hasWhatsAppConfig && !hasHashIsraelConfig) return null;

    return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', marginRight: '10px' }}>
            {/* WhatsApp: disconnected = red flashing chip, connected = green chip */}
            {hasWhatsAppConfig && isWhatsAppConnected === false && (
                <Tooltip title="WhatsApp לא מחובר — לחץ לסריקה">
                    <Chip
                        icon={<WhatsAppIcon />}
                        label="ווטסאפ"
                        size="small"
                        color="default"
                        onClick={handleWhatsAppDisconnectedClick}
                        sx={disconnectedSx}
                    />
                </Tooltip>
            )}

            {hasWhatsAppConfig && isWhatsAppConnected === true && (
                <Tooltip title={isOfficialHubServer(waConfig?.server)
                    ? 'WhatsApp מחובר — לחץ לשיחות'
                    : 'WhatsApp מחובר'}>
                    <Chip
                        icon={<WhatsAppIcon />}
                        label="ווטסאפ"
                        size="small"
                        color="success"
                        onClick={() => {
                            if (isOfficialHubServer(waConfig?.server)) {
                                window.dispatchEvent(new Event('hub-open-drawer'));
                                return;
                            }
                            if (isWhatsAppServerSkipConnectionCheck(waConfig?.server)) {
                                return;
                            }
                            window.dispatchEvent(new Event('wa-open-drawer'));
                        }}
                        sx={{ cursor: 'pointer' }}
                    />
                </Tooltip>
            )}

            {/* QR Dialog for new API */}
            {waConfig?.isNewApi && (
                <WhatsAppQRDialog
                    open={qrDialogOpen}
                    onClose={() => setQrDialogOpen(false)}
                    onConnected={() => {
                        setIsWhatsAppConnected(true);
                        setQrDialogOpen(false);
                    }}
                    baseUrl={waConfig.baseUrl}
                    token={waConfig.token}
                    sessionId={waConfig.instanceId}
                />
            )}

            {/* Hash Israel */}
            {hasHashIsraelConfig && (
                <Tooltip
                    title={
                        hashIsraelState === 'connected'
                            ? 'חשבונית ישראל מחובר — לחץ לאיפוס חיבור אם יש תקלה אצל רשות המסים'
                            : 'חשבונית ישראל לא מחובר — לחץ להתחברות'
                    }
                >
                    <Chip
                        icon={<TagIcon />}
                        label="חשבונית ישראל"
                        size="small"
                        color={hashIsraelState === 'connected' ? 'success' : 'default'}
                        onClick={handleHashIsraelClick}
                        sx={
                            hashIsraelState === 'disconnected'
                                ? disconnectedSx
                                : {
                                      opacity: hashIsraelState === null ? 0.7 : 1,
                                      ...(hashIsraelState === 'connected'
                                          ? { cursor: 'pointer' as const }
                                          : {}),
                                  }
                        }
                    />
                </Tooltip>
            )}

            <Dialog
                open={hashIsraelResetOpen}
                onClose={() => !hashIsraelResetting && setHashIsraelResetOpen(false)}
                dir="rtl"
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>איפוס חשבונית ישראל</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        פעולה זו מוחקת את הטוקן והרענון השמורים בשרת. אחרי האיפוס הסטטוס יוצג כלא מחובר,
                        ותוכלו להתחבר מחדש דרך רשות המסים.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        מומלץ כשהמערכת מציגה &quot;מחובר&quot; אבל חתימה או הנפקה נכשלות בגלל חסימת סשן.
                    </Typography>
                    {hashIsraelResetError ? (
                        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                            {hashIsraelResetError}
                        </Typography>
                    ) : null}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setHashIsraelResetOpen(false)} disabled={hashIsraelResetting}>
                        ביטול
                    </Button>
                    <Button
                        color="warning"
                        variant="contained"
                        onClick={() => void handleHashIsraelResetConfirm()}
                        disabled={hashIsraelResetting}
                    >
                        {hashIsraelResetting ? 'מאפס…' : 'איפוס חיבור'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

const ConnectionIndicators = () => <ConnectionStatusIndicators sx={{ marginRight: '10px' }} />;

export default ConnectionIndicators;
